/**
 * Budget Import Wizard
 *
 * Multi-step Finance worksheet import:
 *   Step 1: Upload & Parse  - file picker, source type breakdown
 *   Step 2: Review Matches  - sub-tabs: Auto-Matched | Suggested | Unmatched | Remove
 *   Step 3: Confirm         - summary, write to DB
 *
 * Designed to handle the Eduvos Finance budget worksheet which is per-employee,
 * not per-position.  Auto-matching uses payroll codes + Financial Dimensions.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Upload, Loader2, CheckCircle, AlertTriangle, Search,
  ChevronRight, ChevronDown, ArrowLeft, ArrowRight, FileSpreadsheet,
  UserCheck, HelpCircle, XCircle, Trash2, Link2, Unlink,
  TrendingDown, Sparkles, Plus, Ruler, RotateCcw, Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  budgetService,
  jobFamilySyncService,
  organizationService,
  FinanceWorksheetItem,
  FinanceWorksheetPreview,
  FinanceWorksheetConfirmResult,
  BudgetImportDraftRecord,
} from '@/services/api/organizationService';
import { DispositionChip, ReconciliationBridgePanel, matchesDispositionFilter, dispositionFilterLabel } from './DispositionChip';
import CreateFoPositionsDialog, { ProvisionSeed, ProvisionGroup } from './CreateFoPositionsDialog';
import UpdateDimensionsDialog, { DimUpdateRow } from './UpdateDimensionsDialog';

// ── Dimension-correction eligibility (per-row + bulk) ─────────────────────
// Current F&O dims aren't carried on suggested_positions — join by fo_position_id
// against listFoPositions() (which exposes them) to compute the delta.
export type FoDims = Map<string, { campus: string; department: string; faculty: string }>;
const DIM_ELIGIBLE_DISP = new Set(['vacant_matched', 'vacancy_nearmiss', 'needs_creation', 'vacancy_contested']);

// Human-friendly timestamp for the "Resume draft from <time>?" prompt.
function formatDraftTime(iso: string | null): string {
  if (!iso) return 'recently';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'recently';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
const _norm = (s?: string | null) => (s || '').trim();

/** The dim delta for a row's top suggestion, or null if no real (unclaimed,
 *  vacant) target with differing dims exists. */
function dimDelta(item: FinanceWorksheetItem, foDims: FoDims): DimUpdateRow | null {
  const top = item.suggested_positions?.[0];
  if (!top || !top.is_vacant || top.is_claimed) return null;
  const cur = foDims.get(top.fo_position_id);
  if (!cur) return null; // current dims unknown -> not eligible (don't show a bogus delta)
  const next = { campus: _norm(item.campus_code), department: _norm(item.department_code), faculty: _norm(item.faculty_code) };
  const old = { campus: _norm(cur.campus), department: _norm(cur.department), faculty: _norm(cur.faculty) };
  if (old.campus === next.campus && old.department === next.department && old.faculty === next.faculty) return null;
  return {
    item, position_id: top.position_id, fo_position_id: top.fo_position_id,
    title: top.title || item.job_title, old, next,
  };
}

function canUpdateDims(item: FinanceWorksheetItem, foDims: FoDims): boolean {
  if (!item.disposition || !DIM_ELIGIBLE_DISP.has(item.disposition)) return false;
  return dimDelta(item, foDims) !== null;
}

// The local position a dim-update would target (the suggested vacant seat — same
// id dimDelta uses). Used to suppress rows already covered by an open change request.
const dimUpdatePid = (i: FinanceWorksheetItem): number => i.suggested_positions?.[0]?.position_id ?? -1;

// Dim-eligible AND not already locked behind an open dim-update change request.
function canUpdateDimsNow(item: FinanceWorksheetItem, foDims: FoDims, pending: Set<number>): boolean {
  return canUpdateDims(item, foDims) && !pending.has(dimUpdatePid(item));
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency', currency: 'ZAR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

function totalMonthly(amounts: Record<number, number>): number {
  return Object.values(amounts).reduce((s, v) => s + (v || 0), 0);
}

function avgMonthly(amounts: Record<number, number>): number {
  const vals = Object.values(amounts).filter(v => v > 0);
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
}

const SOURCE_LABELS: Record<string, string> = {
  existing: 'Existing',
  existing_vacancy: 'Existing Vacancy',
  growth: 'Growth',
  new: 'New',
  remove: 'Remove',
  savings: 'Saving',
  special: 'Special',
};

const SOURCE_COLORS: Record<string, string> = {
  existing: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  existing_vacancy: 'text-amber-700 bg-amber-50 border-amber-200',
  growth: 'text-blue-700 bg-blue-50 border-blue-200',
  new: 'text-purple-700 bg-purple-50 border-purple-200',
  remove: 'text-gray-500 bg-gray-50 border-gray-200',
  savings: 'text-rose-700 bg-rose-50 border-rose-200',
  special: 'text-indigo-700 bg-indigo-50 border-indigo-200',
};

const MATCH_COLORS: Record<string, string> = {
  auto_matched: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  suggested:    'text-amber-700 bg-amber-50 border-amber-200',
  manual:       'text-blue-700 bg-blue-50 border-blue-200',
  unmatched:    'text-red-600 bg-red-50 border-red-200',
  remove:       'text-gray-500 bg-gray-50 border-gray-200',
  savings:      'text-rose-700 bg-rose-50 border-rose-200',
  special:      'text-indigo-700 bg-indigo-50 border-indigo-200',
  skipped:      'text-gray-400 bg-gray-50 border-gray-100',
  pending:      'text-gray-500 bg-gray-50 border-gray-200',
};

type WizardStep = 'upload' | 'review' | 'confirm';

// ── Disposition drill-down table ──────────────────────────────────────────
// Unified, virtualized list of the lines behind a clicked Bridge group/chip —
// any disposition, any match_status, with the same Accept/Skip affordances.
const DISP_GRID = 'grid items-center gap-3 px-3 [grid-template-columns:28px_minmax(150px,2.1fr)_72px_minmax(96px,1fr)_92px_minmax(120px,1.5fr)_150px_148px]';

// Over-demand / no-seat lines that should be provisioned as new F&O budgeted
// vacancies: genuine needs-creation OR contested (a matching seat exists but is
// taken). Both resolve by creating capacity on F&O.
const isCreatable = (i: FinanceWorksheetItem) =>
  i.disposition === 'needs_creation' || i.disposition === 'vacancy_contested'
  || (!i.disposition && !!i.requires_position_creation);

// A terminated leaver whose real seat is ALREADY filled by an active replacement.
// The matcher flags this in disposition_meta.backfilled (history-based, authoritative
// — the leaver's actual last seat now has a live incumbent whose own budget line funds
// it); the is_claimed top suggestion is the legacy fallback for lines lacking history.
// Such a line is redundant: dismiss it (funding it would double-count the headcount).
const isBackfilledTerminated = (i: FinanceWorksheetItem) =>
  i.disposition === 'terminated_incumbent'
  && (((i.disposition_meta as { backfilled?: boolean } | null)?.backfilled === true)
      || !!i.suggested_positions?.[0]?.is_claimed);

// The leaver's seat is gone with no replacement on record — keeping the headcount
// funded means provisioning a fresh position (same resolution as needs-creation).
const isLeaverNoSeat = (i: FinanceWorksheetItem) =>
  i.disposition === 'terminated_incumbent' && !isBackfilledTerminated(i);

// Lines a bulk "Create positions" resolves: genuine needs-creation / contested, OR a
// leaver whose seat is gone (no backfill) and whose budget you want to keep.
// Demand buckets that resolve by provisioning a new position: a wrong-role vacancy
// (near-miss), unmet demand with no usable seat (unresolved gap), or an R0 headcount
// marker the operator wants to establish. (needs_sync is NOT here — it needs a sync,
// not a new position.)
const CREATE_RESOLVABLE_DISP = new Set(['vacancy_nearmiss', 'true_gap', 'headcount_only']);
// Per-row Create+Skip target for the buckets that have no suggestion to Accept.
const isGapCreate = (i: FinanceWorksheetItem) =>
  i.disposition === 'true_gap' || i.disposition === 'headcount_only';
// Any line that offers a "Create" affordance (per-row or bulk).
const isCreateCandidate = (i: FinanceWorksheetItem) =>
  isCreatable(i) || isLeaverNoSeat(i) || (i.disposition ? CREATE_RESOLVABLE_DISP.has(i.disposition) : false);
// Normalized job|campus|dept|faculty key, matched against open NEW_POSITION change
// requests so a line whose create is already staged shows "Creation pending".
const createKey = (i: FinanceWorksheetItem): string =>
  [i.job_title, i.campus_code, i.department_code, i.faculty_code]
    .map(s => (s || '').trim().toLowerCase()).join('|');
const isBulkCreatable = (i: FinanceWorksheetItem) =>
  isCreateCandidate(i)
  // Don't re-create lines the operator already linked (manual) or dropped (skipped).
  && i.match_status !== 'skipped' && i.match_status !== 'manual';
// Contested line whose best-matching seat is already TAKEN by a sibling line —
// the created capacity for its job+dims key is demonstrably consumed, so this
// line needs ANOTHER seat: it bypasses the (quantity-blind) pending/created
// key suppression, per-row AND in the bulk cohort. Each such line stages its
// own NEW_POSITION request (3 lines chasing 1 taken seat ⇒ 2 more seats).
const isContestedTaken = (i: FinanceWorksheetItem) =>
  i.disposition === 'vacancy_contested' && !!i.suggested_positions?.[0]?.is_claimed;

// The seat now occupied by the leaver's replacement (for the "backfilled" evidence).
const backfillSeat = (i: FinanceWorksheetItem): string | undefined =>
  (i.disposition_meta as { backfill_seat?: string } | null)?.backfill_seat
  || i.suggested_positions?.[0]?.fo_position_id;

// Accept only ever targets the matcher's confident assigned seat — never a weak
// or claimed neighbour suggestion (that would mis-link, e.g. Lecturer -> 71% Lab Tech).
const acceptablePid = (i: FinanceWorksheetItem): number | undefined => i.position_id ?? undefined;

const DispositionLines: React.FC<{
  items: FinanceWorksheetItem[];
  foDims: FoDims;
  pendingDimPositions: Set<number>;
  pendingCreateKeys: Set<string>;
  createdKeys: Set<string>;
  selectedKeys: Set<string>;
  keyOf: (item: FinanceWorksheetItem) => string;
  onAccept: (item: FinanceWorksheetItem, positionId: number) => void;
  onSkip: (item: FinanceWorksheetItem) => void;
  onUnskip: (item: FinanceWorksheetItem) => void;
  onCreate: (item: FinanceWorksheetItem) => void;
  onUpdateDims: (item: FinanceWorksheetItem) => void;
  onToggle: (key: string) => void;
  onToggleAll: (checked: boolean, keys: string[]) => void;
}> = ({ items, foDims, pendingDimPositions, pendingCreateKeys, createdKeys, selectedKeys, keyOf, onAccept, onSkip, onUnskip, onCreate, onUpdateDims, onToggle, onToggleAll }) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 12,
  });
  const eligibleKeys = useMemo(() => items.filter(i => canUpdateDimsNow(i, foDims, pendingDimPositions)).map(keyOf), [items, foDims, pendingDimPositions, keyOf]);
  const allSelected = eligibleKeys.length > 0 && eligibleKeys.every(k => selectedKeys.has(k));
  if (items.length === 0) {
    return <div className="py-10 text-center text-sm text-gray-400">No lines match this filter.</div>;
  }
  return (
    <div>
      <div className={cn(DISP_GRID, 'border-b py-2 text-[11px] font-medium text-gray-500')}>
        <input type="checkbox" className="accent-heritage-blue" checked={allSelected}
          disabled={eligibleKeys.length === 0} onChange={e => onToggleAll(e.target.checked, eligibleKeys)}
          title="Select all positions with a fixable dimension mismatch" aria-label="Select all eligible" />
        <span>Job Title</span><span>Source</span><span>Campus · Dept · Faculty</span>
        <span className="text-right">Avg Monthly</span><span>Best Match</span><span>Disposition</span><span>Actions</span>
      </div>
      <div ref={parentRef} className="max-h-[58vh] overflow-y-auto">
        <div style={{ height: v.getTotalSize(), position: 'relative', width: '100%' }}>
          {v.getVirtualItems().map((row) => {
            const item = items[row.index];
            const top = item.suggested_positions?.[0];
            const acceptPid = acceptablePid(item);
            const needsCreate = isCreatable(item);
            const canAct = !needsCreate && (item.match_status === 'suggested' || item.match_status === 'unmatched') && !!acceptPid;
            // Dim mismatch exists, but an open change request already covers it ->
            // show "Pending" instead of re-offering the update.
            const dimPending = canUpdateDims(item, foDims) && pendingDimPositions.has(dimUpdatePid(item));
            const dimEligible = canUpdateDims(item, foDims) && !dimPending;
            const rowKey = keyOf(item);
            // Split terminated_incumbent into its two real sub-states for clarity.
            const isTerminated = item.disposition === 'terminated_incumbent';
            const backfilled = isBackfilledTerminated(item);
            const backfillPos = backfillSeat(item);
            // Near-miss: a vacant seat exists in the dims but the title differs
            // (incl. generic-suffix role mismatches). Make it actionable: link the
            // suggestion after review, provision the correct role, or leave as demand.
            const isNearMiss = item.disposition === 'vacancy_nearmiss';
            // A create for this job+dims is already staged (pending) or done (created) in a
            // NEW_POSITION request -> show that state instead of re-offering Create.
            // QUANTITY carve-out: the created/pending keys are job+dims (quantity-blind) —
            // one created seat would suppress EVERY line of that key forever. A CONTESTED
            // line whose best-matching seat is already TAKEN is chasing capacity a sibling
            // line consumed, so the earlier create is demonstrably used up: keep the
            // per-row Create available for it (bulk create stays conservative).
            const contestedTaken = isContestedTaken(item);
            const createPending = isCreateCandidate(item) && pendingCreateKeys.has(createKey(item));
            const createDone = isCreateCandidate(item) && !createPending && !contestedTaken
              && createdKeys.has(createKey(item));
            // Operator already acted on this line: dismissed (skipped) or linked (manual).
            const resolved = item.match_status === 'skipped' ? 'skipped'
              : item.match_status === 'manual' ? 'manual' : null;
            return (
              <div
                key={`${item.employee_code}|${item.job_title}|${item.source_type}|${row.index}`}
                data-index={row.index}
                ref={v.measureElement}
                className={cn(DISP_GRID, 'absolute left-0 top-0 w-full border-b py-2 text-xs hover:bg-gray-50',
                  resolved === 'skipped' && 'opacity-50 [&_.disp-title]:line-through')}
                style={{ transform: `translateY(${row.start}px)` }}
              >
                <input type="checkbox" className="accent-heritage-blue disabled:opacity-30" checked={selectedKeys.has(rowKey)}
                  disabled={!dimEligible} onChange={() => onToggle(rowKey)}
                  title={dimEligible ? 'Select for bulk dimension update' : 'No fixable dimension mismatch'} aria-label="Select row" />
                <div className="min-w-0">
                  <div className="disp-title truncate font-medium" title={item.job_title}>{item.job_title || '—'}</div>
                  {(item.employee_name || item.employee_code) && (
                    <div className="truncate text-[11px] text-gray-400">{[item.employee_name, item.employee_code].filter(Boolean).join(' · ')}</div>
                  )}
                  {item.last_seat_context && (() => {
                    const ls = item.last_seat_context!;
                    const reused = !!item.matched_position_code && item.matched_position_code === ls.fo_position_id;
                    const note = ls.status === 'RETIRED' ? 'retired' : ls.is_vacant ? 'vacant' : 'taken';
                    return (
                      <div className={cn('truncate text-[11px]', reused ? 'text-emerald-600' : 'text-amber-600')}
                        title={reused
                          ? "Re-used the worker's own vacated seat"
                          : `The worker currently holds no F&O position. Their last seat was ${ls.fo_position_id} (${ls.title}) at ${ls.campus} — ${note}. Different dimensions to this budget line, so it wasn't auto-re-used; decide whether they moved or the line's campus is wrong.`}>
                        {reused ? '✓ re-used last seat ' : '↩ last seat '}{ls.fo_position_id} · {ls.title} · {ls.campus} · {note}{ls.vacated_on ? ` · left ${ls.vacated_on}` : ''}
                      </div>
                    );
                  })()}
                </div>
                <div><Badge variant="outline" className={cn('text-[9px]', SOURCE_COLORS[item.source_type])}>{SOURCE_LABELS[item.source_type] || item.source_type}</Badge></div>
                <div className="truncate text-gray-500" title={`${item.campus_code || '—'} / ${item.department_code || '—'} / ${item.faculty_code || '—'}`}>
                  {[item.campus_code, item.department_code, item.faculty_code].filter(Boolean).join(' · ') || '—'}
                </div>
                <div className="text-right font-mono tabular-nums">{formatCurrency(avgMonthly(item.monthly_amounts))}</div>
                <div className="min-w-0">
                  {backfilled ? (
                    <>
                      <div className="truncate text-gray-500">Replacement already in seat</div>
                      <div className="truncate text-[11px] text-gray-400 tabular-nums" title="A live worker already holds the leaver's former seat — funded by their own line">
                        {backfillPos || '—'} · backfilled
                      </div>
                    </>
                  ) : top ? (
                    <>
                      <div className="truncate" title={top.title}>{top.title}</div>
                      {top.is_claimed ? (
                        <div className="truncate text-[11px] text-amber-600 tabular-nums" title="This seat is already taken by another budget line (over-demand)">
                          {top.fo_position_id} · taken ({Math.round(top.score * 100)}%)
                        </div>
                      ) : top.role_mismatch ? (
                        <div className="truncate text-[11px] text-amber-600 tabular-nums" title="Shares only a generic role word (e.g. 'Administrator') — likely a different role. Verify before linking.">
                          ⚠ {top.fo_position_id}{top.is_vacant ? ' (vacant)' : ''} · different role? · {Math.round(top.score * 100)}%
                        </div>
                      ) : (
                        <div className="truncate text-[11px] text-gray-400 tabular-nums">
                          {top.fo_position_id}{top.is_vacant ? ' (vacant)' : ''} · {Math.round(top.score * 100)}%
                        </div>
                      )}
                    </>
                  ) : <span className="text-gray-300">—</span>}
                </div>
                <div>
                  {isTerminated ? (
                    <DispositionChip
                      label={backfilled ? 'Backfilled — dismiss' : 'Leaver — no seat'}
                      tone={backfilled ? 'slate' : 'blue'}
                      title={backfilled
                        ? `The leaver's seat${backfillPos ? ` ${backfillPos}` : ''} is already filled by a replacement whose own line funds it — this line is redundant.`
                        : "The leaver's seat is gone and no replacement is on record — create a position to keep the budget, or dismiss."} />
                  ) : (
                    <DispositionChip disposition={item.disposition} />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {resolved ? (
                    <span className="inline-flex items-center gap-2">
                      <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium',
                        resolved === 'skipped' ? 'text-gray-400' : 'text-emerald-600')}>
                        {resolved === 'skipped'
                          ? <><XCircle className="h-3.5 w-3.5" /> Dismissed</>
                          : <><Link2 className="h-3.5 w-3.5" /> Linked</>}
                      </span>
                      <button className="text-heritage-blue hover:underline text-[11px]"
                        onClick={() => onUnskip(item)} title="Undo — restore this line for review">Undo</button>
                    </span>
                  ) : createPending ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 whitespace-nowrap"
                      title="A create request for this job + dimensions is already pending approval — approve or reject it on the Requests tab before staging another.">
                      <Clock className="h-3 w-3" /> Creation pending
                    </span>
                  ) : createDone ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 whitespace-nowrap"
                      title="A position for this job + dimensions was already created via an approved request — re-run the import preview and this line should match the new seat.">
                      <CheckCircle className="h-3 w-3" /> Created
                    </span>
                  ) : needsCreate ? (
                    <button className="inline-flex items-center gap-1 text-heritage-blue hover:underline"
                      onClick={() => onCreate(item)}>
                      <Plus className="h-3.5 w-3.5" /> Create
                    </button>
                  ) : canAct ? (
                    <>
                      <button className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                        onClick={() => onAccept(item, acceptPid!)}>
                        <Link2 className="h-3.5 w-3.5" /> Accept
                      </button>
                      <button className="text-gray-400 hover:text-gray-600" onClick={() => onSkip(item)}>Skip</button>
                    </>
                  ) : isTerminated ? (
                    backfilled ? (
                      <button className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700" onClick={() => onSkip(item)}
                        title={`Replacement already in seat${backfillPos ? ` ${backfillPos}` : ''} — drop this redundant line (funding it double-counts the headcount)`}>
                        <XCircle className="h-3.5 w-3.5" /> Dismiss
                      </button>
                    ) : (
                      <>
                        <button className="inline-flex items-center gap-1 text-heritage-blue hover:underline" onClick={() => onCreate(item)}
                          title="The leaver's seat is gone — provision a replacement position on F&O to keep this budget">
                          <Plus className="h-3.5 w-3.5" /> Create
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" onClick={() => onSkip(item)}
                          title="Drop this line (don't keep the budget)">Dismiss</button>
                      </>
                    )
                  ) : isNearMiss && top ? (
                    <>
                      <button className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                        onClick={() => onAccept(item, top.position_id)}
                        title={top.role_mismatch
                          ? `Link to ${top.fo_position_id} anyway — only if you've confirmed it IS the same role`
                          : `Link this line to the suggested vacant seat ${top.fo_position_id}`}>
                        <Link2 className="h-3.5 w-3.5" /> Accept
                      </button>
                      <button className="inline-flex items-center gap-1 text-heritage-blue hover:underline" onClick={() => onCreate(item)}
                        title="Provision the correct-role position instead of linking">
                        <Plus className="h-3.5 w-3.5" /> Create
                      </button>
                      <button className="text-gray-400 hover:text-gray-600" onClick={() => onSkip(item)}
                        title="Leave as unfunded demand">Skip</button>
                    </>
                  ) : isGapCreate(item) ? (
                    <>
                      <button className="inline-flex items-center gap-1 text-heritage-blue hover:underline" onClick={() => onCreate(item)}
                        title={item.disposition === 'headcount_only'
                          ? 'Establish a position for this R0 headcount marker'
                          : 'Provision a position for this unmet demand (no usable seat exists)'}>
                        <Plus className="h-3.5 w-3.5" /> Create
                      </button>
                      <button className="text-gray-400 hover:text-gray-600" onClick={() => onSkip(item)}
                        title="Leave as demand / drop this line">Skip</button>
                    </>
                  ) : !dimEligible && !dimPending ? (
                    <span className="text-[11px] text-gray-400">{item.matched_position_code || '—'}</span>
                  ) : null}
                  {dimEligible && !resolved && (
                    <button className="inline-flex items-center gap-1 text-heritage-blue hover:underline"
                      onClick={() => onUpdateDims(item)} title="Correct this position's dimensions to the budget values">
                      <Ruler className="h-3.5 w-3.5" /> Update dims
                    </button>
                  )}
                  {dimPending && !resolved && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 whitespace-nowrap"
                      title="A dimension-update change request is already pending approval for this position — approve or reject it before staging another">
                      <Clock className="h-3 w-3" /> Pending change
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface BudgetImportWizardProps {
  /** Called after a successful import to refresh budget summary */
  onImportComplete?: () => void;
}

// ── Main Component ───────────────────────────────────────────────────────

const BudgetImportWizard: React.FC<BudgetImportWizardProps> = ({ onImportComplete }) => {
  // Wizard state
  const [step, setStep] = useState<WizardStep>('upload');
  const [isUploading, setIsUploading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Data
  const [preview, setPreview] = useState<FinanceWorksheetPreview | null>(null);
  const [items, setItems] = useState<FinanceWorksheetItem[]>([]);
  const [result, setResult] = useState<FinanceWorksheetConfirmResult | null>(null);
  const [budgetYear, setBudgetYear] = useState<number>(new Date().getFullYear());

  // Review sub-tab
  const [reviewTab, setReviewTab] = useState<string>('auto_matched');
  // Disposition drill-down — set by clicking a Bridge group/chip. When active,
  // the review tables are replaced by a unified list of exactly those lines.
  const [dispFilter, setDispFilter] = useState<string | null>(null);
  // Seed for the "create position" provisioning dialog (from a needs-creation line).
  const [createSeed, setCreateSeed] = useState<ProvisionSeed | null>(null);
  // Dimension correction: current F&O dims (for the delta), row selection, dialog batch.
  const [foDims, setFoDims] = useState<FoDims>(new Map());
  const [selectedItemKeys, setSelectedItemKeys] = useState<Set<string>>(new Set());
  const [updateDimsRows, setUpdateDimsRows] = useState<DimUpdateRow[] | null>(null);
  // Positions already covered by an OPEN dim-update change request — shown as
  // "pending" and not re-submittable (server is the source of truth, so it
  // survives reload; refreshed after each successful stage).
  const [pendingDimPositions, setPendingDimPositions] = useState<Set<number>>(new Set());
  // job|campus|dept|faculty keys covered by a NEW_POSITION change request — pending
  // (draft/awaiting approval) vs created (approved/implemented — positions now exist).
  // Both suppress re-create; the row shows "Creation pending" vs "Created".
  const [pendingCreateKeys, setPendingCreateKeys] = useState<Set<string>>(new Set());
  const [createdKeys, setCreatedKeys] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');

  // When checked on Review step, the server wipes existing budget_year data
  // before inserting new rows. Useful after parser fixes / for a clean reload —
  // avoids the "staged items pile up" duplication on second import.
  const [replaceExisting, setReplaceExisting] = useState(false);

  // ── Draft persistence (autosave + resume) ────────────────────────────
  // The upload + per-row triage is autosaved server-side (one draft per
  // operator+year) so a reload / navigation / different machine resumes instead
  // of starting over. updated_at drives the "Resume draft from <time>?" prompt.
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [pendingDraft, setPendingDraft] = useState<BudgetImportDraftRecord | null>(null);
  const draftSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Skip the autosave that would otherwise fire immediately after a resume
  // (re-saving exactly what we just loaded).
  const suppressAutosave = useRef(false);

  // ── Derived data ─────────────────────────────────────────────────────

  const categorized = useMemo(() => {
    const auto_matched = items.filter(i => i.match_status === 'auto_matched');
    const suggested = items.filter(i => i.match_status === 'suggested');
    const unmatched = items.filter(i =>
      i.match_status === 'unmatched' || i.match_status === 'pending'
    );
    const remove = items.filter(i => i.match_status === 'remove');
    const savings = items.filter(i => i.match_status === 'savings');
    const special = items.filter(i => i.match_status === 'special');
    const manual = items.filter(i => i.match_status === 'manual');
    const skipped = items.filter(i => i.match_status === 'skipped');
    return { auto_matched, suggested, unmatched, remove, savings, special, manual, skipped };
  }, [items]);

  const confirmableCount = useMemo(() => {
    return items.filter(i =>
      i.match_status === 'auto_matched' ||
      i.match_status === 'manual' ||
      (i.match_status === 'suggested' && i.position_id)
    ).length;
  }, [items]);

  const stagingCount = useMemo(() => {
    return items.filter(i =>
      i.match_status === 'unmatched' || i.match_status === 'pending' ||
      (i.match_status === 'suggested' && !i.position_id)
    ).length;
  }, [items]);

  const needsCreationCount = useMemo(() => {
    return items.filter(i => i.requires_position_creation).length;
  }, [items]);

  // Detect over-link: multiple confirmable items mapping to the same position_id.
  // Server will reject if egregious; show client-side warning before user clicks
  // Confirm so they can recover without round-tripping a 400.
  const overLinkInfo = useMemo(() => {
    const counts = new Map<number, number>();
    for (const i of items) {
      if (!i.position_id) continue;
      if (i.match_status !== 'auto_matched' && i.match_status !== 'manual'
          && !(i.match_status === 'suggested' && i.position_id)) continue;
      counts.set(i.position_id, (counts.get(i.position_id) ?? 0) + 1);
    }
    const dup_positions: Array<{ position_id: number; count: number }> = [];
    let egregious = 0;
    for (const [pid, cnt] of counts.entries()) {
      if (cnt > 1) {
        dup_positions.push({ position_id: pid, count: cnt });
        if (cnt > 3) egregious++;
      }
    }
    dup_positions.sort((a, b) => b.count - a.count);
    return { dup_positions, egregious, dup_count: dup_positions.length };
  }, [items]);

  // Client-side fix: clear position_id from items beyond the FIRST per position,
  // and re-mark them as 'unmatched'. Preserves the matcher's intent — the first
  // claimant keeps the link, subsequent collisions go back to the queue.
  const dedupePositions = useCallback(() => {
    const seen = new Set<number>();
    setItems(prev => prev.map(item => {
      if (!item.position_id) return item;
      if (item.match_status !== 'auto_matched' && item.match_status !== 'manual'
          && !(item.match_status === 'suggested' && item.position_id)) return item;
      if (!seen.has(item.position_id)) {
        seen.add(item.position_id);
        return item;
      }
      return { ...item, position_id: null, match_status: 'unmatched' as const, match_confidence: 0 };
    }));
  }, []);

  const totalBudgetAmount = useMemo(() => {
    // Savings rows are intentionally included — they represent negative
    // amounts in the worksheet, so summing all non-skipped/non-remove rows
    // gives the net Finance budget impact.
    return items
      .filter(i => i.match_status !== 'remove' && i.match_status !== 'skipped' && i.match_status !== 'special')
      .reduce((sum, i) => sum + totalMonthly(i.monthly_amounts), 0);
  }, [items]);

  // ── Step 1: Upload ───────────────────────────────────────────────────

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);
    setResult(null);
    try {
      const data = await budgetService.previewFinanceWorksheet(file, budgetYear);
      setPreview(data);
      setItems(data.items);
      setStep('review');
      // Default to the tab with most items
      if (data.auto_matched > 0) setReviewTab('auto_matched');
      else if (data.suggested > 0) setReviewTab('suggested');
      else setReviewTab('unmatched');
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Upload failed';
      setUploadError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  }, [budgetYear]);

  // ── Step 2: Review actions ───────────────────────────────────────────
  // Use item identity (employee_code + job_title + source_type) as a stable key
  // so that filtering doesn't break index-based lookups.

  const itemKey = (item: FinanceWorksheetItem) =>
    `${item.employee_code}|${item.job_title}|${item.source_type}|${item.campus_code}|${item.department_code}`;

  // Drop a row from the dimension-update selection once it's acted on.
  const _deselect = (key: string) =>
    setSelectedItemKeys(prev => { if (!prev.has(key)) return prev; const n = new Set(prev); n.delete(key); return n; });

  const acceptSuggestion = useCallback((item: FinanceWorksheetItem, positionId: number) => {
    const key = itemKey(item);
    setItems(prev => prev.map(it =>
      itemKey(it) === key
        ? { ...it, position_id: positionId, match_status: 'manual' as const, match_confidence: 1.0 }
        : it
    ));
    _deselect(key);
  }, []);

  const skipItem = useCallback((item: FinanceWorksheetItem) => {
    const key = itemKey(item);
    setItems(prev => prev.map(it =>
      itemKey(it) === key
        ? { ...it, match_status: 'skipped' as const, position_id: null }
        : it
    ));
    _deselect(key);
  }, []);

  const unskipItem = useCallback((item: FinanceWorksheetItem) => {
    const key = itemKey(item);
    setItems(prev => prev.map(it =>
      itemKey(it) === key
        ? { ...it, match_status: 'unmatched' as const }
        : it
    ));
  }, []);

  // Apply-all within the active disposition filter: accept every actionable line
  // (suggested/unmatched with a deduped position) to its matcher pick. Trusts
  // item.position_id (Pass-2 deduped) over suggested_positions[0] to avoid over-link.
  const bulkAcceptFiltered = useCallback(() => {
    if (!dispFilter) return;
    setItems(prev => prev.map(it => {
      if (!matchesDispositionFilter(it.disposition, dispFilter)) return it;
      if (it.match_status !== 'suggested' && it.match_status !== 'unmatched') return it;
      const pid = acceptablePid(it);
      if (!pid) return it;
      return { ...it, position_id: pid, match_status: 'manual' as const, match_confidence: 1.0 };
    }));
  }, [dispFilter]);

  // Counts for the active filter's bulk actions.
  const dispCohortCounts = useMemo(() => {
    if (!dispFilter) return { total: 0, acceptable: 0, needsCreate: 0, termBackfilled: 0 };
    let total = 0, acceptable = 0, needsCreate = 0, termBackfilled = 0;
    for (const i of items) {
      if (!matchesDispositionFilter(i.disposition, dispFilter)) continue;
      total++;
      if ((i.match_status === 'suggested' || i.match_status === 'unmatched') && acceptablePid(i)) acceptable++;
      // Bulk "Create positions" now also covers leavers whose seat is gone, and
      // contested-taken lines (over-demand — the created seat is consumed).
      if (isBulkCreatable(i)
          && (isContestedTaken(i)
              || (!pendingCreateKeys.has(createKey(i)) && !createdKeys.has(createKey(i))))) needsCreate++;
      // Terminated worker whose old seat is already filled by a replacement (whose
      // own line funds it) -> this line is redundant, offer a one-click dismiss.
      // Exclude lines already dismissed/linked so the count reflects what's left.
      if (isBackfilledTerminated(i) && i.match_status !== 'skipped' && i.match_status !== 'manual') termBackfilled++;
    }
    return { total, acceptable, needsCreate, termBackfilled };
  }, [items, dispFilter, pendingCreateKeys, createdKeys]);

  // Bulk-dismiss terminated lines whose seat is backfilled (taken) — redundant.
  const bulkDismissBackfilled = useCallback(() => {
    if (!dispFilter) return;
    setItems(prev => prev.map(it => {
      if (!matchesDispositionFilter(it.disposition, dispFilter)) return it;
      if (!isBackfilledTerminated(it)) return it;
      return { ...it, match_status: 'skipped' as const, position_id: null };
    }));
  }, [dispFilter]);

  // Open the provisioning dialog seeded from a single needs-creation line.
  const openCreateForItem = useCallback((item: FinanceWorksheetItem) => {
    setCreateSeed({
      title: item.job_title || undefined,
      campus: item.campus_code || undefined,
      department: item.department_code || undefined,
      faculty: item.faculty_code || undefined,
      employment_category: item.employment_category || undefined,
      count: 1,
      business_justification: `Budget ${preview?.budget_year ?? ''} — ${item.budget_code || item.job_title || 'demand'}`,
    });
    _deselect(itemKey(item));
  }, [preview]);

  // Open the provisioning dialog for the whole filter cohort. Group by JOB TITLE +
  // dimensions so each batch keeps BOTH its own job and its own campus/dept/faculty —
  // mixed-job cohorts (leaver-no-seat, near-miss, gap) must never be collapsed onto a
  // single job/dim. The dialog stages one request per group under that group's own job.
  const openCreateForCohort = useCallback(() => {
    if (!dispFilter) return;
    const cohort = items.filter(i => matchesDispositionFilter(i.disposition, dispFilter) && isBulkCreatable(i)
      && (isContestedTaken(i)
          || (!pendingCreateKeys.has(createKey(i)) && !createdKeys.has(createKey(i)))));
    if (!cohort.length) return;
    const groupMap = new Map<string, ProvisionGroup>();
    const titleCount = new Map<string, number>();
    for (const i of cohort) {
      const title = i.job_title || '';
      const campus = i.campus_code || '';
      const department = i.department_code || '';
      const faculty = i.faculty_code || '';
      // Split by employment type too, so a Permanent and an FTC of the same job+dims
      // are staged as separate requests with their OWN appointment type.
      const empCat = i.employment_category || '';
      const key = `${title}|${campus}|${department}|${faculty}|${empCat}`;
      const g = groupMap.get(key);
      if (g) g.count += 1;
      else groupMap.set(key, {
        campus: campus || undefined,
        department: department || undefined,
        faculty: faculty || undefined,
        title: title || undefined,
        employment_category: empCat || undefined,
        count: 1,
      });
      titleCount.set(title, (titleCount.get(title) || 0) + 1);
    }
    // Fallback title (most common) only seeds the optional fallback Job picker —
    // each group still resolves + stages under its OWN title.
    let title = '', max = 0;
    for (const [t, c] of titleCount) if (c > max) { max = c; title = t; }
    setCreateSeed({
      title: title || undefined,
      business_justification: `Budget ${preview?.budget_year ?? ''} — needs creation`,
      groups: Array.from(groupMap.values()),
    });
  }, [dispFilter, items, preview, pendingCreateKeys, createdKeys]);

  // ── Dimension correction ─────────────────────────────────────────────
  // Load current F&O dims once on the review step (suggestions don't carry them).
  useEffect(() => {
    if (step !== 'review') return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await jobFamilySyncService.listFoPositions();
        if (cancelled) return;
        const m: FoDims = new Map();
        for (const p of resp.items || []) {
          m.set(p.position_id, {
            campus: p.dimension_campus || '', department: p.dimension_department || '', faculty: p.dimension_faculty || '',
          });
        }
        setFoDims(m);
      } catch { /* dims unknown -> update-dims simply not eligible */ }
    })();
    return () => { cancelled = true; };
  }, [step]);

  // Positions already covered by an open dim-update change request (refreshable so
  // a stage immediately removes those rows from the eligible set).
  const loadPendingDims = useCallback(async () => {
    try {
      const ids = await organizationService.getPendingDimensionPositions();
      setPendingDimPositions(new Set(ids));
    } catch { /* best-effort */ }
  }, []);
  // Keys already covered by an open NEW_POSITION request (refreshable so staging a
  // create immediately marks those lines "Creation pending").
  const loadPendingCreates = useCallback(async () => {
    try {
      const { pending, created } = await organizationService.getPendingCreationKeys();
      setPendingCreateKeys(new Set(pending));
      setCreatedKeys(new Set(created));
    } catch { /* best-effort */ }
  }, []);
  useEffect(() => { if (step === 'review') { loadPendingDims(); loadPendingCreates(); } }, [step, loadPendingDims, loadPendingCreates]);

  const toggleSelect = useCallback((key: string) => {
    setSelectedItemKeys(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);
  const toggleSelectAll = useCallback((checked: boolean, keys: string[]) => {
    setSelectedItemKeys(prev => { const n = new Set(prev); for (const k of keys) { checked ? n.add(k) : n.delete(k); } return n; });
  }, []);
  const openUpdateDimsForItems = useCallback((batch: FinanceWorksheetItem[]) => {
    const rows = batch.map(it => dimDelta(it, foDims)).filter(Boolean) as DimUpdateRow[];
    if (rows.length) setUpdateDimsRows(rows);
  }, [foDims]);

  // Selected, dim-eligible rows within the active filter (drives the bulk button).
  const selectedDimsItems = useMemo(() => {
    if (!dispFilter) return [] as FinanceWorksheetItem[];
    return items.filter(i => matchesDispositionFilter(i.disposition, dispFilter) && selectedItemKeys.has(itemKey(i)) && canUpdateDimsNow(i, foDims, pendingDimPositions));
  }, [items, dispFilter, selectedItemKeys, foDims, pendingDimPositions]);

  const bulkAcceptAutoMatched = useCallback(() => {
    // Auto-matched items are already in correct state - this is a no-op but good for UX clarity
    // They will be sent as-is during confirm
  }, []);

  const bulkAcceptSuggestions = useCallback(() => {
    setItems(prev => prev.map(item => {
      if (item.match_status !== 'suggested') return item;
      // CRITICAL: trust the matcher's per-row position_id (Pass 1.5 / Pass 2
      // already deduped via claimed_positions). Earlier this used
      // suggested_positions[0] blindly, which for dim-fallback rows in the
      // same campus|dept|faculty produced identical top-suggestion lists —
      // so e.g. 95 PT/ACATEA Lecturer rows all overwrote their unique
      // matcher-assigned position with the same one, link-clobbering.
      // Only fall back to the top suggestion when the matcher didn't
      // assign anything (defensive — shouldn't happen for 'suggested').
      if (item.position_id) {
        return { ...item, match_status: 'manual' as const };
      }
      const top = item.suggested_positions?.[0];
      if (top) {
        return { ...item, position_id: top.position_id, match_status: 'manual' as const, match_confidence: top.score };
      }
      return item;
    }));
  }, []);

  // ── Step 3: Confirm ──────────────────────────────────────────────────

  const handleConfirm = useCallback(async () => {
    if (!preview) return;
    if (replaceExisting) {
      const ok = window.confirm(
        `Replace ALL existing ${preview.budget_year} budget data?\n\n` +
        `This will delete every staged item AND every linked budget row for ${preview.budget_year} ` +
        `before importing this file. Other years are NOT affected.\n\n` +
        `Click OK to proceed.`,
      );
      if (!ok) return;
    }
    setIsConfirming(true);
    try {
      const res = await budgetService.confirmFinanceWorksheet({
        batch_id: preview.batch_id,
        budget_year: preview.budget_year,
        items,
        replace_existing: replaceExisting,
      });
      setResult(res);
      setStep('confirm');
      onImportComplete?.();
      // Import persisted — drop the autosaved draft (backend also clears it).
      setDraftSavedAt(null);
      try { await budgetService.deleteImportDraft(preview.budget_year); } catch { /* backend also deletes */ }
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Confirm failed';
      setUploadError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsConfirming(false);
    }
  }, [preview, items, onImportComplete, replaceExisting]);

  const handleReset = useCallback(() => {
    setStep('upload');
    setPreview(null);
    setItems([]);
    setResult(null);
    setUploadError(null);
    setSearchFilter('');
    setDraftSavedAt(null);
    // Note: we do NOT delete the draft here — returning to upload re-offers it
    // via the resume prompt, so "start over" never silently loses work. Only an
    // explicit Discard or a successful Confirm clears it.
  }, []);

  // ── Draft: resume / discard ──────────────────────────────────────────

  const resumeDraft = useCallback(() => {
    if (!pendingDraft) return;
    const p = pendingDraft.payload;
    suppressAutosave.current = true;   // don't re-save what we just loaded
    setPreview(p.preview);
    setItems(p.items || []);
    if (p.ui?.reviewTab) setReviewTab(p.ui.reviewTab);
    setDispFilter(p.ui?.dispFilter ?? null);
    setReplaceExisting(!!p.ui?.replaceExisting);
    setStep('review');
    setDraftSavedAt(pendingDraft.updated_at);
    setPendingDraft(null);
  }, [pendingDraft]);

  const discardDraft = useCallback(async () => {
    const yr = pendingDraft?.budget_year ?? budgetYear;
    setPendingDraft(null);
    try { await budgetService.deleteImportDraft(yr); } catch { /* non-fatal */ }
  }, [pendingDraft, budgetYear]);

  // On the upload step, look for a saved draft for the selected year and offer
  // to resume it. Cleared once a preview is active (resumed or freshly uploaded).
  useEffect(() => {
    if (step !== 'upload' || preview) { setPendingDraft(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const d = await budgetService.getImportDraft(budgetYear);
        if (!cancelled) setPendingDraft(d && d.payload?.items?.length ? d : null);
      } catch { /* no resume offer on error */ }
    })();
    return () => { cancelled = true; };
  }, [budgetYear, step, preview]);

  // Debounced autosave of the live draft whenever the upload or triage changes.
  useEffect(() => {
    if (!preview) return;                       // nothing to save until a worksheet is loaded
    if (suppressAutosave.current) { suppressAutosave.current = false; return; }
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(async () => {
      try {
        await budgetService.saveImportDraft(preview.budget_year, {
          preview, items,
          ui: { reviewTab, dispFilter, replaceExisting },
        });
        setDraftSavedAt(new Date().toISOString());
      } catch { /* autosave is best-effort */ }
    }, 1200);
    return () => { if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current); };
  }, [preview, items, reviewTab, dispFilter, replaceExisting]);

  // ── Filter helper for review tables ──────────────────────────────────

  const filterItems = useCallback((list: FinanceWorksheetItem[]) => {
    if (!searchFilter) return list;
    const q = searchFilter.toLowerCase();
    return list.filter(i =>
      i.job_title?.toLowerCase().includes(q) ||
      i.employee_name?.toLowerCase().includes(q) ||
      i.matched_position_code?.toLowerCase().includes(q) ||
      i.suggested_positions?.some(s => s.fo_position_id?.toLowerCase().includes(q)) ||
      i.budget_code?.toLowerCase().includes(q) ||
      i.employee_code?.toLowerCase().includes(q) ||
      i.campus_code?.toLowerCase().includes(q) ||
      i.department_code?.toLowerCase().includes(q)
    );
  }, [searchFilter]);

  // ── Step Progress Bar ────────────────────────────────────────────────

  const stepIndex = step === 'upload' ? 0 : step === 'review' ? 1 : 2;
  const progress = ((stepIndex + 1) / 3) * 100;

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Step indicator */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className={cn(stepIndex >= 0 && 'text-heritage-blue font-medium')}>
            1. Upload & Parse
          </span>
          <span className={cn(stepIndex >= 1 && 'text-heritage-blue font-medium')}>
            2. Review Matches
          </span>
          <span className={cn(stepIndex >= 2 && 'text-heritage-blue font-medium')}>
            3. Confirm
          </span>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      {/* ── STEP 1: Upload ────────────────────────────────────────────── */}
      {step === 'upload' && (
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Finance Budget Worksheet Import
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingDraft && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-3">
                <RotateCcw className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-amber-900">
                    Resume your {pendingDraft.budget_year} import draft?
                  </div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    {pendingDraft.payload?.items?.length ?? 0} rows · last saved {formatDraftTime(pendingDraft.updated_at)}.
                    Pick up your triage where you left off instead of re-uploading.
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      className="h-7 bg-heritage-blue text-white hover:bg-heritage-blue/90"
                      onClick={resumeDraft}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Resume draft
                    </Button>
                    <Button size="sm" variant="outline" className="h-7" onClick={discardDraft}>
                      Discard
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <p className="text-sm text-gray-600">
              Upload the Finance budget worksheet (Excel). The system will auto-detect
              columns, classify rows by source type (Existing, Vacancy, Growth, New, Remove),
              and match team members to positions using payroll codes and Financial Dimensions.
            </p>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Budget Year:</span>
                <Select value={String(budgetYear)} onValueChange={v => setBudgetYear(Number(v))}>
                  <SelectTrigger className="w-[100px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027, 2028].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <label className="cursor-pointer">
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                <div className={cn(
                  "inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium",
                  "bg-heritage-blue text-white hover:bg-heritage-blue/90 cursor-pointer transition-colors",
                  isUploading && "opacity-50 cursor-not-allowed",
                )}>
                  {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {isUploading ? 'Parsing...' : 'Choose Finance Worksheet'}
                </div>
              </label>
            </div>

            {isUploading && (
              <div className="bg-blue-50 border border-blue-100 rounded p-3 text-sm text-blue-700 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Parsing worksheet and auto-matching team members to positions... This may take a minute.
              </div>
            )}

            {uploadError && (
              <div className="bg-red-50 border border-red-100 rounded p-3 text-sm text-red-700">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                {uploadError}
              </div>
            )}

            {/* Result summary from previous import */}
            {result && (
              <div className="bg-emerald-50 border border-emerald-100 rounded p-4 space-y-2">
                <div className="flex items-center gap-2 text-emerald-700 font-medium">
                  <CheckCircle className="h-5 w-5" />
                  Import Complete
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-2xl font-bold text-emerald-600">{result.budgets_created}</div>
                    <div className="text-gray-500">Budget records created</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-600">{result.needs_matching ?? result.staging_created}</div>
                    <div className="text-gray-500">Staged for manual matching</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-400">{result.skipped}</div>
                    <div className="text-gray-500">Skipped</div>
                  </div>
                </div>
                {result.errors.length > 0 && (
                  <div className="text-sm text-red-600 mt-2">
                    {result.errors.slice(0, 5).map((err, i) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Existing simple import below */}
            <div className="border-t pt-4 mt-4">
              <p className="text-xs text-gray-400 mb-1">
                Or use the simple position-code import (for monthly actuals or direct position budgets).
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── STEP 2: Review Matches ────────────────────────────────────── */}
      {step === 'review' && preview && (
        <div className="space-y-3">
          {/* Summary bar */}
          <Card>
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">{preview.filename}</span>
                  <Badge variant="outline" className="text-xs">{preview.budget_year}</Badge>
                  <span className="text-xs text-gray-500">{preview.total_rows} rows parsed</span>
                  {draftSavedAt && (
                    <span className="text-xs text-emerald-600 flex items-center gap-1" title={`Autosaved ${formatDraftTime(draftSavedAt)}`}>
                      <CheckCircle className="h-3 w-3" /> Draft autosaved
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={handleReset}>
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleConfirm}
                    disabled={isConfirming || confirmableCount === 0}
                  >
                    {isConfirming
                      ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      : <CheckCircle className="h-4 w-4 mr-1" />
                    }
                    Confirm Import ({confirmableCount} matched)
                  </Button>
                </div>
              </div>
              {/* Replace-existing toggle: surgical wipe of just this year's
                  data before insert. Operator gets a confirm() prompt at click. */}
              <div className="mt-3 pt-3 border-t flex items-start gap-2">
                <input
                  type="checkbox"
                  id="replace-existing"
                  checked={replaceExisting}
                  onChange={(e) => setReplaceExisting(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300"
                />
                <label htmlFor="replace-existing" className="text-xs select-none cursor-pointer">
                  <span className={cn("font-medium", replaceExisting ? "text-amber-700" : "text-slate-700")}>
                    Replace existing {preview.budget_year} budget data
                  </span>
                  <span className="block text-[10px] text-gray-500 mt-0.5">
                    Deletes ALL staged items + linked budget rows for {preview.budget_year} before importing this file.
                    Other years are NOT affected. Use after parser fixes or to start fresh.
                  </span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Multi-row-per-position banner.
              - Egregious (>3 rows on same position): RED warning, server will reject,
                offer Dedupe button.
              - Split-line (2-3 rows on same position): NEUTRAL info note, allowed
                through (legitimate worksheet pattern — one position funded from
                multiple cost-centre rows or duplicate worksheet entries). */}
          {overLinkInfo.dup_count > 0 && overLinkInfo.egregious > 0 && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-rose-800">
                    {overLinkInfo.egregious} position{overLinkInfo.egregious === 1 ? '' : 's'} have &gt;3 budget rows linking to them — Confirm will be rejected
                  </div>
                  <div className="text-[10px] text-rose-700 mt-0.5">
                    Top offenders: {overLinkInfo.dup_positions.filter(d => d.count > 3).slice(0, 5).map(d => `pos ${d.position_id} (${d.count} rows)`).join(', ')}
                  </div>
                  <div className="text-[10px] text-rose-700 mt-1">
                    Click below to keep the first claimant per position and re-stage the rest as Unmatched:
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs mt-2 border-rose-300 text-rose-700 hover:bg-rose-100"
                    onClick={dedupePositions}
                  >
                    Dedupe — keep first per position
                  </Button>
                </div>
              </div>
            </div>
          )}
          {overLinkInfo.dup_count > 0 && overLinkInfo.egregious === 0 && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-2">
              <div className="text-[11px] text-blue-700 flex items-center gap-1.5">
                <HelpCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  <span className="font-medium">{overLinkInfo.dup_count} positions</span> have 2-3 budget rows linking to them — usually legitimate split-line worksheets
                  (same team member with cost-centre splits, or campus transfers). Allowed through. Top: {overLinkInfo.dup_positions.slice(0, 3).map(d => `pos ${d.position_id} (${d.count})`).join(', ')}
                </span>
              </div>
            </div>
          )}

          {/* Sync-gap banner — surfaces F&O PNs not in our local users table.
              Distinct from "needs position created" (Lecturer pool) — these
              are real workers whose User record is missing. */}
          {preview.missing_user_codes && preview.missing_user_codes.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-amber-800">
                    {preview.missing_user_codes.length} budget {preview.missing_user_codes.length === 1 ? 'row references a worker' : 'rows reference workers'} not in our local user database
                  </div>
                  <div className="text-[10px] text-amber-700 mt-0.5">
                    Likely a sync gap. Run a F&amp;O user re-sync, then re-import to auto-match these. Sample:
                  </div>
                  <div className="text-[10px] text-amber-700 mt-1 font-mono space-y-0.5 max-h-20 overflow-y-auto">
                    {preview.missing_user_codes.slice(0, 8).map((m) => (
                      <div key={m.employee_code}>
                        {m.employee_code} · {m.employee_name || '—'} · {m.job_title} · {m.campus_code}/{m.department_code}
                      </div>
                    ))}
                    {preview.missing_user_codes.length > 8 && (
                      <div className="italic">…and {preview.missing_user_codes.length - 8} more</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reconciliation Bridge leads — the single foots-to-gross summary.
              Click any group or chip to drill into exactly those lines. */}
          <ReconciliationBridgePanel preview={preview} onFilter={setDispFilter} activeFilter={dispFilter} />

          {/* Search filter (applies to whichever view is active) */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Filter by name, title, campus, department, position no..."
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              className="pl-9 h-8 text-sm"
            />
          </div>

          {dispFilter ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-heritage-blue/5 px-3 py-2 text-xs">
                <span>
                  Showing <span className="font-medium tabular-nums">{dispCohortCounts.total}</span>{' '}
                  {dispositionFilterLabel(dispFilter)} line(s)
                </span>
                <div className="flex items-center gap-2">
                  {dispCohortCounts.acceptable > 0 && (
                    <Button size="sm" className="h-7" onClick={bulkAcceptFiltered}>
                      <CheckCircle className="h-3.5 w-3.5 mr-1" /> Accept all ({dispCohortCounts.acceptable})
                    </Button>
                  )}
                  {dispCohortCounts.needsCreate > 0 && (
                    <Button size="sm" variant="outline" className="h-7" onClick={openCreateForCohort}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Create positions ({dispCohortCounts.needsCreate})
                    </Button>
                  )}
                  {dispCohortCounts.termBackfilled > 0 && (
                    <Button size="sm" variant="outline" className="h-7" onClick={bulkDismissBackfilled}
                      title="Dismiss terminated lines whose seat is already backfilled by a replacement">
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss backfilled ({dispCohortCounts.termBackfilled})
                    </Button>
                  )}
                  {selectedDimsItems.length > 0 && (
                    <Button size="sm" variant="outline" className="h-7" onClick={() => openUpdateDimsForItems(selectedDimsItems)}
                      title="Correct the selected positions' dimensions to the budget values">
                      <Ruler className="h-3.5 w-3.5 mr-1" /> Update dims ({selectedDimsItems.length} selected)
                    </Button>
                  )}
                  <button className="inline-flex items-center gap-1 text-heritage-blue hover:underline" onClick={() => setDispFilter(null)}>
                    <XCircle className="h-3.5 w-3.5" /> Clear
                  </button>
                </div>
              </div>
              <Card>
                <CardContent className="p-0">
                  <DispositionLines
                    items={filterItems(items.filter(i => matchesDispositionFilter(i.disposition, dispFilter)))}
                    foDims={foDims}
                    pendingDimPositions={pendingDimPositions}
                    pendingCreateKeys={pendingCreateKeys}
                    createdKeys={createdKeys}
                    selectedKeys={selectedItemKeys}
                    keyOf={itemKey}
                    onAccept={acceptSuggestion}
                    onSkip={skipItem}
                    onUnskip={unskipItem}
                    onCreate={openCreateForItem}
                    onUpdateDims={(it) => openUpdateDimsForItems([it])}
                    onToggle={toggleSelect}
                    onToggleAll={toggleSelectAll}
                  />
                </CardContent>
              </Card>
            </>
          ) : (
          <>
          {/* Review filter — pick a category to triage. Compact by design: the
              Bridge above is the summary, so these are just navigation. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-muted-foreground">Review:</span>
            {[
              { key: 'auto_matched', label: 'Auto-matched', n: categorized.auto_matched.length, icon: <UserCheck className="h-3.5 w-3.5" /> },
              ...(categorized.manual.length > 0 ? [{ key: 'manual', label: 'Manual', n: categorized.manual.length, icon: <Link2 className="h-3.5 w-3.5" /> }] : []),
              { key: 'suggested', label: 'Suggested', n: categorized.suggested.length, icon: <HelpCircle className="h-3.5 w-3.5" /> },
              { key: 'unmatched', label: 'Unmatched', n: categorized.unmatched.length, icon: <Unlink className="h-3.5 w-3.5" /> },
              { key: 'remove', label: 'Remove', n: categorized.remove.length, icon: <Trash2 className="h-3.5 w-3.5" /> },
              { key: 'savings', label: 'Savings', n: categorized.savings.length, icon: <TrendingDown className="h-3.5 w-3.5" /> },
              { key: 'special', label: 'Special', n: categorized.special.length, icon: <Sparkles className="h-3.5 w-3.5" /> },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => { setReviewTab(t.key); setSearchFilter(''); }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors',
                  reviewTab === t.key ? 'border-heritage-blue bg-heritage-blue text-white' : 'hover:bg-gray-50',
                )}
              >
                {t.icon}
                {t.label}
                <span className={cn('tabular-nums', reviewTab === t.key ? 'text-white/80' : 'text-muted-foreground')}>{t.n}</span>
                {t.key === 'unmatched' && needsCreationCount > 0 && (
                  <span className={cn(reviewTab === t.key ? 'text-white/80' : 'text-amber-600')}>· {needsCreationCount} need pos.</span>
                )}
              </button>
            ))}
          </div>

          {/* Review tables */}
          <Card>
            <CardContent className="p-0">
              {reviewTab === 'auto_matched' && (
                <AutoMatchedTable
                  items={filterItems(categorized.auto_matched)}
                  totalCount={categorized.auto_matched.length}
                />
              )}
              {reviewTab === 'manual' && (
                <AutoMatchedTable
                  items={filterItems(categorized.manual)}
                  totalCount={categorized.manual.length}
                />
              )}
              {reviewTab === 'suggested' && (
                <SuggestedTable
                  items={filterItems(categorized.suggested)}
                  totalCount={categorized.suggested.length}
                  onAccept={acceptSuggestion}
                  onSkip={skipItem}
                  onBulkAccept={bulkAcceptSuggestions}
                />
              )}
              {reviewTab === 'unmatched' && (
                <UnmatchedTable
                  items={filterItems(categorized.unmatched)}
                  totalCount={categorized.unmatched.length}
                  onSkip={skipItem}
                />
              )}
              {reviewTab === 'remove' && (
                <RemoveTable
                  items={filterItems(categorized.remove)}
                  totalCount={categorized.remove.length}
                />
              )}
              {reviewTab === 'savings' && (
                <SavingsTable
                  items={filterItems(categorized.savings)}
                  totalCount={categorized.savings.length}
                />
              )}
              {reviewTab === 'special' && (
                <SpecialTable
                  items={filterItems(categorized.special)}
                  totalCount={categorized.special.length}
                />
              )}
            </CardContent>
          </Card>
          </>
          )}

          {/* Skipped items */}
          {categorized.skipped.length > 0 && (
            <div className="text-xs text-gray-400 flex items-center gap-2">
              <XCircle className="h-3.5 w-3.5" />
              {categorized.skipped.length} items skipped
              <button
                className="underline hover:text-gray-600"
                onClick={() => {
                  // Unskip all
                  setItems(prev => prev.map(item =>
                    item.match_status === 'skipped'
                      ? { ...item, match_status: 'unmatched' as const }
                      : item
                  ));
                }}
              >
                Restore all
              </button>
            </div>
          )}

          {/* Confirm bar at bottom */}
          <div className="flex items-center justify-between bg-gray-50 rounded-md border p-3">
            <div className="text-sm text-gray-600">
              <span className="font-medium text-emerald-600">{confirmableCount}</span> matched
              {stagingCount > 0 && (
                <> &middot; <span className="font-medium text-amber-600">{stagingCount}</span> will be staged for manual matching</>
              )}
              {categorized.skipped.length > 0 && (
                <> &middot; <span className="text-gray-400">{categorized.skipped.length} skipped</span></>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-500 font-mono">{formatCurrency(totalBudgetAmount)} total annual</span>
              <Button
                onClick={handleConfirm}
                disabled={isConfirming || confirmableCount === 0}
                className="gap-1.5"
              >
                {isConfirming
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <CheckCircle className="h-4 w-4" />
                }
                Confirm Import
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 3: Confirm / Result ──────────────────────────────────── */}
      {step === 'confirm' && result && (
        <Card>
          <CardContent className="py-6 px-6 space-y-4">
            <div className="text-center space-y-2">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto" />
              <h3 className="text-lg font-semibold text-gray-900">Import Complete</h3>
              <p className="text-sm text-gray-500">
                Budget data from <span className="font-medium">{preview?.filename}</span> has been processed.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">{result.budgets_created}</div>
                <div className="text-xs text-gray-500">Budget Records</div>
                <div className="text-[10px] text-gray-400">Linked to positions</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">{result.needs_matching ?? result.staging_created}</div>
                <div className="text-xs text-gray-500">Staged Items</div>
                <div className="text-[10px] text-gray-400">Needs manual matching</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-400">{result.skipped}</div>
                <div className="text-xs text-gray-500">Skipped</div>
                <div className="text-[10px] text-gray-400">Not imported</div>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="bg-red-50 border border-red-100 rounded p-3 text-sm max-w-lg mx-auto">
                <div className="font-medium text-red-700 mb-1">Errors ({result.errors.length}):</div>
                <div className="max-h-[120px] overflow-y-auto">
                  {result.errors.map((err, i) => (
                    <div key={i} className="text-xs text-red-600 py-0.5">{err}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center pt-2 flex items-center justify-center gap-3">
              <Button variant="outline" onClick={handleReset}>
                <Upload className="h-4 w-4 mr-1" /> Import Another File
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  handleReset();
                  // Switch parent tab to Overview — the BudgetTab listens for onImportComplete
                  // which already refreshes the summary. User just needs to click Overview tab.
                }}
              >
                Done
              </Button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-1">
              Switch to the <span className="font-medium">Overview</span> tab to see budget breakdowns.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Provision positions for needs-creation lines — reuses the Jobs-tab flow
          (stages a request to the Requests tab; F&O positions created on approval). */}
      <CreateFoPositionsDialog
        open={!!createSeed}
        onOpenChange={(o) => { if (!o) setCreateSeed(null); }}
        job={null}
        seed={createSeed}
        onStaged={() => { setCreateSeed(null); loadPendingCreates(); }}
      />

      {/* Correct under-specified F&O position dimensions to the budget values. */}
      {updateDimsRows && (
        <UpdateDimensionsDialog
          rows={updateDimsRows}
          budgetYear={preview?.budget_year}
          onClose={() => setUpdateDimsRows(null)}
          onStaged={() => { setUpdateDimsRows(null); setSelectedItemKeys(new Set()); loadPendingDims(); }}
        />
      )}
    </div>
  );
};

// ── Sub-Components: Review Tables ────────────────────────────────────────

/** Auto-matched items — employees matched by payroll code */
const AutoMatchedTable: React.FC<{
  items: FinanceWorksheetItem[];
  totalCount: number;
}> = ({ items, totalCount }) => {
  if (totalCount === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No auto-matched items. Team Members are matched by payroll code.
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-2 bg-emerald-50/50 border-b flex items-center justify-between">
        <div className="text-xs text-emerald-700 font-medium flex items-center gap-1.5">
          <UserCheck className="h-3.5 w-3.5" />
          {totalCount} team members matched by payroll code
          {items.length < totalCount && (
            <span className="text-gray-400 font-normal ml-1">(showing {items.length} filtered)</span>
          )}
        </div>
        <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200">
          Will import automatically
        </Badge>
      </div>
      <div className="max-h-[400px] overflow-y-auto border-t">
        <Table>
          <TableHeader>
            <TableRow className="text-[11px]">
              <TableHead className="w-[90px]">Team Member Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Job Title</TableHead>
              <TableHead className="w-[60px]">Campus</TableHead>
              <TableHead className="w-[80px]">Dept</TableHead>
              <TableHead className="w-[180px]">Matched Position</TableHead>
              <TableHead className="text-right w-[90px]">Avg Monthly</TableHead>
              <TableHead className="text-right w-[100px]">Annual Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.slice(0, 200).map((item, idx) => (
              <TableRow key={idx} className="text-xs">
                <TableCell className="font-mono">{item.employee_code}</TableCell>
                <TableCell className="font-medium">{item.employee_name || '—'}</TableCell>
                <TableCell className="truncate max-w-[180px]" title={item.job_title}>{item.job_title}</TableCell>
                <TableCell>{item.campus_code || '—'}</TableCell>
                <TableCell>{item.department_code || '—'}</TableCell>
                <TableCell>
                  {item.matched_position_code ? (
                    <div className="text-[10px]">
                      <span className="font-mono text-gray-500">{item.matched_position_code}</span>
                      {item.matched_position_title && (
                        <span className="ml-1 text-gray-700" title={item.matched_position_title}>
                          {item.matched_position_title.length > 25
                            ? item.matched_position_title.slice(0, 25) + '...'
                            : item.matched_position_title}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(avgMonthly(item.monthly_amounts))}</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(totalMonthly(item.monthly_amounts))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items.length > 200 && (
          <div className="text-xs text-gray-400 text-center py-2">
            Showing first 200 of {items.length} items
          </div>
        )}
      </div>
    </div>
  );
};

/** Suggested matches — vacancies matched by Financial Dimensions + title */
const SuggestedTable: React.FC<{
  items: FinanceWorksheetItem[];
  totalCount: number;
  onAccept: (item: FinanceWorksheetItem, positionId: number) => void;
  onSkip: (item: FinanceWorksheetItem) => void;
  onBulkAccept: () => void;
}> = ({ items, totalCount, onAccept, onSkip, onBulkAccept }) => {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  // Sort by top-suggestion score descending so highest-confidence matches
  // appear first. Falls back to item.match_confidence if no suggestions.
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const scoreA = a.suggested_positions?.[0]?.score ?? a.match_confidence ?? 0;
      const scoreB = b.suggested_positions?.[0]?.score ?? b.match_confidence ?? 0;
      return scoreB - scoreA;
    });
  }, [items]);

  if (totalCount === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No suggested matches. Vacancies are matched by Financial Dimensions and job title.
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-2 bg-amber-50/50 border-b flex items-center justify-between">
        <div className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
          <HelpCircle className="h-3.5 w-3.5" />
          {totalCount} items with suggested matches
          {items.length < totalCount && (
            <span className="text-gray-400 font-normal ml-1">(showing {items.length} filtered)</span>
          )}
        </div>
        <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={onBulkAccept}>
          Accept All Top Suggestions
        </Button>
      </div>
      <div className="max-h-[400px] overflow-y-auto border-t">
        <Table>
          <TableHeader>
            <TableRow className="text-[11px]">
              <TableHead className="w-8"></TableHead>
              <TableHead>Job Title</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="w-[60px]">Campus</TableHead>
              <TableHead className="w-[80px]">Department</TableHead>
              <TableHead className="w-[60px]">Faculty</TableHead>
              <TableHead className="w-[60px]">Grade</TableHead>
              <TableHead className="text-right w-[100px]">Avg Monthly</TableHead>
              <TableHead className="w-[120px]">Best Match</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedItems.slice(0, 100).map((item, displayIdx) => {
              const isExpanded = expandedIdx === displayIdx;
              // Display the matcher's actual deduped pick (item.position_id),
              // not the per-row highest-scoring suggestion. For dim-fallback
              // rows in the same campus|dept|faculty, suggested_positions[0]
              // is identical across rows but Pass 2 deduped them across
              // unique vacancies — so showing/accepting the top suggestion
              // would link many rows to the same position. Find the matcher's
              // pick in the suggestion list (it's always present, just may
              // not be at index 0) and use that as the "Best Match".
              const matcherPick = item.position_id
                ? item.suggested_positions?.find(s => s.position_id === item.position_id)
                : null;
              const topSuggestion = matcherPick ?? item.suggested_positions?.[0];
              return (
                <React.Fragment key={displayIdx}>
                  <TableRow className="text-xs">
                    <TableCell className="pr-0">
                      {item.suggested_positions?.length > 0 && (
                        <button onClick={() => setExpandedIdx(isExpanded ? null : displayIdx)}>
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                            : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                          }
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[240px]">
                      <div className="font-medium truncate" title={item.job_title}>{item.job_title}</div>
                      {(item.employee_name || item.employee_code) && (
                        <div className="text-[10px] text-gray-500 truncate">
                          {item.employee_name && <span>{item.employee_name}</span>}
                          {item.employee_name && item.employee_code && <span> · </span>}
                          {item.employee_code && <span className="font-mono">{item.employee_code}</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn("text-[9px]", SOURCE_COLORS[item.source_type])}>
                        {SOURCE_LABELS[item.source_type] || item.source_type}
                      </Badge>
                    </TableCell>
                    <TableCell>{item.campus_code || '—'}</TableCell>
                    <TableCell>{item.department_code || '—'}</TableCell>
                    <TableCell>{item.faculty_code || '—'}</TableCell>
                    <TableCell>{item.grade || '—'}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(avgMonthly(item.monthly_amounts))}</TableCell>
                    <TableCell>
                      {topSuggestion ? (
                        <div className="text-[10px]">
                          <div className="font-medium truncate" title={topSuggestion.title}>{topSuggestion.title}</div>
                          <div className="text-gray-400">
                            {topSuggestion.fo_position_id}
                            {topSuggestion.is_vacant && <span className="ml-1 text-amber-500">(vacant)</span>}
                            <span
                              className="ml-1 cursor-help underline decoration-dotted decoration-gray-300 underline-offset-2"
                              title={topSuggestion.match_reason || `Score: ${Math.round(topSuggestion.score * 100)}%`}
                            >
                              {Math.round(topSuggestion.score * 100)}%
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-300">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {topSuggestion && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10px] text-emerald-600 hover:text-emerald-700"
                            onClick={() => onAccept(item, topSuggestion.position_id)}
                          >
                            <Link2 className="h-3 w-3 mr-0.5" />Accept
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[10px] text-gray-400 hover:text-gray-600"
                          onClick={() => onSkip(item)}
                        >
                          Skip
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {/* Expanded row — show all suggestions */}
                  {isExpanded && item.suggested_positions?.length > 0 && (
                    <TableRow className="bg-amber-50/30">
                      <TableCell colSpan={10} className="p-2 pl-10">
                        <div className="text-[10px] text-gray-500 mb-1 font-medium flex items-center gap-2">
                          <span>All suggestions ({item.suggested_positions.length}):</span>
                          {item.employee_name && (
                            <span className="text-gray-700">
                              for <span className="font-semibold">{item.employee_name}</span>
                              {item.employee_code && <span className="font-mono text-gray-400 ml-1">({item.employee_code})</span>}
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {item.suggested_positions.map((sug, sIdx) => (
                            <div key={sIdx} className="flex items-center justify-between bg-white rounded border px-2 py-1">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <span className="font-mono text-gray-400">{sug.fo_position_id}</span>
                                <span className="font-medium truncate">{sug.title}</span>
                                {sug.is_vacant && (
                                  <Badge variant="outline" className="text-[9px] text-amber-500 border-amber-200">Vacant</Badge>
                                )}
                                {sug.match_reason && (
                                  <span className="text-[9px] text-gray-400 italic truncate" title={sug.match_reason}>
                                    {sug.match_reason}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span
                                  className="text-gray-400 cursor-help underline decoration-dotted decoration-gray-300 underline-offset-2"
                                  title={sug.match_reason || `Score: ${Math.round(sug.score * 100)}%`}
                                >
                                  {Math.round(sug.score * 100)}% match
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-5 px-2 text-[10px] text-emerald-600"
                                  onClick={() => {
                                    onAccept(item, sug.position_id);
                                    setExpandedIdx(null);
                                  }}
                                >
                                  Select
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {items.length > 100 && (
          <div className="text-xs text-gray-400 text-center py-2">
            Showing first 100 of {items.length} items
          </div>
        )}
      </div>
    </div>
  );
};

/** Unmatched items — no position found */
const UnmatchedTable: React.FC<{
  items: FinanceWorksheetItem[];
  totalCount: number;
  onSkip: (item: FinanceWorksheetItem) => void;
}> = ({ items, totalCount, onSkip }) => {
  if (totalCount === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-300" />
        All items matched! No unmatched rows.
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-2 bg-red-50/50 border-b flex items-center justify-between">
        <div className="text-xs text-red-600 font-medium flex items-center gap-1.5">
          <Unlink className="h-3.5 w-3.5" />
          {totalCount} items without a position match
          {items.length < totalCount && (
            <span className="text-gray-400 font-normal ml-1">(showing {items.length} filtered)</span>
          )}
        </div>
        <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">
          Will be staged for manual matching
        </Badge>
      </div>
      <div className="max-h-[400px] overflow-y-auto border-t">
        <Table>
          <TableHeader>
            <TableRow className="text-[11px]">
              <TableHead>Team Member Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Job Title</TableHead>
              <TableHead>Disposition</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="w-[60px]">Campus</TableHead>
              <TableHead className="w-[80px]">Department</TableHead>
              <TableHead className="w-[60px]">Grade</TableHead>
              <TableHead className="text-right w-[100px]">Avg Monthly</TableHead>
              <TableHead className="w-[60px]">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.slice(0, 200).map((item, idx) => (
              <TableRow key={idx} className="text-xs">
                <TableCell className="font-mono">{item.employee_code || '—'}</TableCell>
                <TableCell>{item.employee_name || '—'}</TableCell>
                <TableCell className="font-medium truncate max-w-[180px]" title={item.job_title}>
                  {item.job_title}
                </TableCell>
                <TableCell><DispositionChip disposition={item.disposition} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className={cn("text-[9px]", SOURCE_COLORS[item.source_type])}>
                      {SOURCE_LABELS[item.source_type] || item.source_type}
                    </Badge>
                    {item.requires_position_creation && (
                      <Badge variant="outline" className="text-[9px] text-amber-700 bg-amber-50 border-amber-200" title="No vacant F&O position available — needs to be created">
                        Needs Position
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{item.campus_code || '—'}</TableCell>
                <TableCell>{item.department_code || '—'}</TableCell>
                <TableCell>{item.grade || '—'}</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(avgMonthly(item.monthly_amounts))}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-gray-400 hover:text-gray-600"
                    onClick={() => onSkip(item)}
                  >
                    Skip
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items.length > 200 && (
          <div className="text-xs text-gray-400 text-center py-2">
            Showing first 200 of {items.length} items
          </div>
        )}
      </div>
    </div>
  );
};

/** Remove items — informational, positions flagged for retirement */
// Generic informational table — used for Remove, Savings, and Special rows
// (none of which create position-budget records, all of which are kept for
// audit/visibility).
const InformationalTable: React.FC<{
  items: FinanceWorksheetItem[];
  totalCount: number;
  emptyMessage: string;
  headerLabel: string;
  headerIcon: React.ReactNode;
  showAmount?: boolean;
  amountClassName?: (amount: number) => string;
}> = ({ items, totalCount, emptyMessage, headerLabel, headerIcon, showAmount, amountClassName }) => {
  if (totalCount === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
        <div className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
          {headerIcon}
          {totalCount} {headerLabel}
          {items.length < totalCount && (
            <span className="text-gray-400 font-normal ml-1">(showing {items.length} filtered)</span>
          )}
        </div>
        <Badge variant="outline" className="text-[10px] text-gray-400">
          Informational only — no budget records created
        </Badge>
      </div>
      <div className="max-h-[300px] overflow-y-auto border-t">
        <Table>
          <TableHeader>
            <TableRow className="text-[11px]">
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Job Title</TableHead>
              <TableHead className="w-[60px]">Grade</TableHead>
              <TableHead className="w-[60px]">Campus</TableHead>
              <TableHead className="w-[80px]">Department</TableHead>
              {showAmount && <TableHead className="w-[110px] text-right">Annual Amount</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.slice(0, 100).map((item, idx) => {
              const annual = totalMonthly(item.monthly_amounts);
              return (
                <TableRow key={idx} className="text-xs text-gray-500">
                  <TableCell className="font-mono">{item.budget_code || item.employee_code || '—'}</TableCell>
                  <TableCell>{item.employee_name || '—'}</TableCell>
                  <TableCell className="truncate max-w-[200px]" title={item.job_title}>{item.job_title}</TableCell>
                  <TableCell>{item.grade || '—'}</TableCell>
                  <TableCell>{item.campus_code || '—'}</TableCell>
                  <TableCell>{item.department_code || '—'}</TableCell>
                  {showAmount && (
                    <TableCell className={cn('text-right font-mono', amountClassName?.(annual))}>
                      {annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const RemoveTable: React.FC<{
  items: FinanceWorksheetItem[];
  totalCount: number;
}> = ({ items, totalCount }) => (
  <InformationalTable
    items={items}
    totalCount={totalCount}
    emptyMessage="No items flagged for removal in this worksheet."
    headerLabel="positions flagged for retirement review"
    headerIcon={<Trash2 className="h-3.5 w-3.5" />}
  />
);

const SavingsTable: React.FC<{
  items: FinanceWorksheetItem[];
  totalCount: number;
}> = ({ items, totalCount }) => (
  <InformationalTable
    items={items}
    totalCount={totalCount}
    emptyMessage="No savings (budget release) lines in this worksheet."
    headerLabel="budget release lines (negative amounts reduce the annual budget)"
    headerIcon={<TrendingDown className="h-3.5 w-3.5" />}
    showAmount
    amountClassName={(amount) => amount < 0 ? 'text-rose-700' : 'text-gray-500'}
  />
);

const SpecialTable: React.FC<{
  items: FinanceWorksheetItem[];
  totalCount: number;
}> = ({ items, totalCount }) => (
  <InformationalTable
    items={items}
    totalCount={totalCount}
    emptyMessage="No special (non-standard) budget lines in this worksheet."
    headerLabel="non-standard budget lines"
    headerIcon={<Sparkles className="h-3.5 w-3.5" />}
    showAmount
  />
);

export default BudgetImportWizard;
